import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { executeMutation, executeQuery } from '../../graphql';
import { validateCustomer, getAddressDetails, validateCustomerByCardNumber } from '../../graphql/mutations.js';
import { checkOwnership } from '../../graphql/queries.js';
import Wizard from '../../components/Wizard';
import AddressLookup from '../../components/AddressLookup';
import Tooltip from '../../components/Tooltip';
import CardNumberHelp from '../../components/CardNumberHelp';
import { usePageContent } from '../../hooks/usePageContent';
import { PRODUCT_TYPE, plateItemFromOrder, trackBeginCheckout } from '../../utils/analytics';
import styles from './index.module.css';

const UNIT_TYPES = [
  { value: 'APT', label: 'Apartment' },
  { value: 'BERTH', label: 'Berth' },
  { value: 'BLOCK', label: 'Block' },
  { value: 'BUILD', label: 'Building' },
  { value: 'CABIN', label: 'Cabin' },
  { value: 'CARAVAN', label: 'Caravan' },
  { value: 'CLUSTER', label: 'Cluster' },
  { value: 'CTGE', label: 'Cottage' },
  { value: 'DUP', label: 'Duplex' },
  { value: 'DWELLING', label: 'Dwelling' },
  { value: 'FY', label: 'Factory' },
  { value: 'F', label: 'Flat' },
  { value: 'HANGAR', label: 'Hangar' },
  { value: 'HOS UNIT', label: 'Hostil Unit' },
  { value: 'HSE', label: 'House' },
  { value: 'HUT', label: 'Hut' },
  { value: 'KSK', label: 'Kiosk' },
  { value: 'MSNT', label: 'Maisonette' },
  { value: 'MB', label: 'Marine Berth' },
  { value: 'OFF', label: 'Office' },
  { value: 'PTHS', label: 'Penthouse' },
  { value: 'RESDNCE', label: 'Residence' },
  { value: 'R', label: 'Room' },
  { value: 'SECTION', label: 'Section' },
  { value: 'SHED', label: 'Shed' },
  { value: 'SHOP', label: 'Shop' },
  { value: 'SITE', label: 'Site' },
  { value: 'SL', label: 'Stall' },
  { value: 'STORE', label: 'Store' },
  { value: 'STU', label: 'Studio' },
  { value: 'SE', label: 'Suite' },
  { value: 'TENANCY', label: 'Tenancy' },
  { value: 'TNHS', label: 'Townhouse' },
  { value: 'U', label: 'Unit' },
  { value: 'VLLA', label: 'Villa' },
  { value: 'WARD', label: 'Ward' },
  { value: 'WE', label: 'Warehouse' },
  { value: 'WRKSHOP', label: 'Workshop' },
];

const STREET_TYPES = [
  { value: 'ALLY', label: 'Alley' },
  { value: 'ARC', label: 'Arcade' },
  { value: 'AVE', label: 'Avenue' },
  { value: 'BVD', label: 'Boulevard' },
  { value: 'CL', label: 'Close' },
  { value: 'CD', label: 'Court' },
  { value: 'CRES', label: 'Crescent' },
  { value: 'DR', label: 'Drive' },
  { value: 'ESP', label: 'Esplanade' },
  { value: 'GR', label: 'Grove' },
  { value: 'HWY', label: 'Highway' },
  { value: 'LANE', label: 'Lane' },
  { value: 'PDE', label: 'Parade' },
  { value: 'PL', label: 'Place' },
  { value: 'RD', label: 'Road' },
  { value: 'SQ', label: 'Square' },
  { value: 'ST', label: 'Street' },
  { value: 'TCE', label: 'Terrace' },
];

const EMPTY_ADDRESS = {
  address: '',
  addressId: '',
  unitType: '',
  unitNumber: '',
  floorType: '',
  floorNumber: '',
  streetOrLot: 'Street number',
  streetLotNumber: '',
  streetName: '',
  streetType: '',
  suburb: '',
  postcode: '',
  state: 'VIC',
};

const RestyleIdentificationPage = () => {
  const navigate = useNavigate();
  const { find } = usePageContent('restyle-identification');
  const wizardContent = find('wizard')             || {};
  const c             = find('identificationForm') || {};
  const fe = c.fieldErrors      || {};
  const ve = c.validationErrors || {};
  const f  = c.fields           || {};

  const session = (() => { try { return JSON.parse(sessionStorage.getItem('plateOrderData') || '{}'); } catch { return {}; } })();
  const combination = session.combination || '';
  const vehicleType = session.vehicleType || 'car';

  const wizardSteps = wizardContent.steps || [
    { title: 'Check combination', href: `/restyle/check-combination?combination=${combination}&vehicleType=${vehicleType}` },
    { title: 'Select a style', href: '/restyle/select-a-style' },
    { title: 'Confirm ownership' },
    { title: 'Checkout', href: '/restyle/checkout' },
  ];

  useEffect(() => {
    if (!sessionStorage.getItem('plateOrderData')) {
      navigate('/restyle/select-a-style');
    }
  }, [navigate]);

  // GA: begin_checkout on page load (prod: identificationForm.js). Once per mount (StrictMode-safe),
  // and only when a style has actually been chosen.
  const beginCheckoutTrackedRef = useRef(false);
  useEffect(() => {
    if (beginCheckoutTrackedRef.current) return;
    beginCheckoutTrackedRef.current = true;
    try {
      const order = JSON.parse(window.sessionStorage.getItem('plateOrderData') || 'null');
      if (order?.variantId) trackBeginCheckout(plateItemFromOrder(order, PRODUCT_TYPE.RESTYLE), order.price);
    } catch { /* analytics must never block the page */ }
  }, []);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    licenceNumber: '',
    cardNumber: '',
    ...EMPTY_ADDRESS,
    newsletterOptIn: false,
  });

  const [errors, setErrors] = useState({});
  // Snapshot of `errors` taken on submit; only submit (and a verification tab switch) changes it
  const [summaryErrors, setSummaryErrors] = useState({});
  const errorSummaryRef = useRef(null);
  const shouldFocusErrors = useRef(false);
  const [verificationMethod, setVerificationMethod] = useState('address');
  const [showManualAddress, setShowManualAddress] = useState(false);
  const [showFloorNumber, setShowFloorNumber] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationError, setValidationError] = useState('');

  const FIELD_META = {
    firstName:     { label: "Plate owner's first name",                      href: '#manual-first-name' },
    lastName:      { label: "Plate owner's last name",                       href: '#manual-last-name' },
    email:         { label: 'Email address',                   href: '#manual-email-address' },
    phone:         { label: 'Phone number',                    href: '#manual-phone' },
    licenceNumber: { label: 'Victorian driver licence number', href: '#manual-licence-number' },
    cardNumber:    { label: 'Driver licence card number',      href: '#manual-card-number' },
    address:       { label: 'Address',                         href: '#manual-address-line-1' },
  };

  const handleErrorClick = (e) => {
    e.preventDefault();
    const targetId = e.currentTarget.getAttribute('href')?.slice(1);
    const el = targetId && document.getElementById(targetId);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (shouldFocusErrors.current && Object.keys(summaryErrors).length > 0 && errorSummaryRef.current) {
      errorSummaryRef.current.focus();
      shouldFocusErrors.current = false;
    }
  }, [summaryErrors]);

  const getFieldError = (name, value) => {
    switch (name) {
      case 'firstName':
        return !value ? (fe.firstName || 'Please provide a first name') : '';
      case 'lastName':
        return !value ? (fe.lastName || 'Please provide a last name') : '';
      case 'email':
        if (!value) return fe.email || 'Please provide an email address';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return fe.emailInvalid || 'Please enter a valid email address, E.g. email@mail.com';
        return '';
      case 'phone':
        if (!value) return fe.phone || 'Please provide a phone number';
        if (!/^(((0|\+61)(2|4|3|7|8))|(1300|1800)){0,1}([\s-]*([0-9][\s-]*){8,15}){1}$/.test(value))
          return fe.phoneInvalid || 'Please enter a valid phone number';
        return '';
      case 'licenceNumber':
        if (!value) return fe.licenceNumber || 'Please provide a licence number';
        if (!/^\d{3,9}$/.test(value)) return fe.licenceNumberInvalid || 'Please enter a valid license number that contains 3 to 9 numeric characters. E.g. 001234567';
        return '';
      case 'cardNumber':
        if (verificationMethod !== 'cardNumber') return '';
        if (!value) return fe.cardNumber || 'Enter your card number';
        if (!/^[DP][0-9]{7}$/.test(value)) return fe.cardNumberInvalid || 'Enter a valid card number (1 letter followed by 7 numbers. E.g.: P1234567)';
        return '';
      case 'address':
        if (verificationMethod !== 'address' || showManualAddress) return '';
        if (!value) return fe.address || 'Please provide an address line';
        if (!formData.addressId) return fe.addressNotFromList || 'Please use a suggested value from the drop down list';
        return '';
      default:
        return '';
    }
  };

  const setFieldError = (name, error) => {
    setErrors(prev => {
      const next = { ...prev };
      if (error) next[name] = error;
      else delete next[name];
      return next;
    });
  };

  const dropSummaryError = (name) => {
    setSummaryErrors(prev => {
      if (!(name in prev)) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setFieldError(name, getFieldError(name, value));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    setFormData(prev => ({ ...prev, [name]: newValue }));
    if (errors[name] !== undefined) {
      setFieldError(name, getFieldError(name, newValue));
    }
  };

  const buildManualAddress = () => {
    const fmt = (val) => val?.trim() ? `${val.trim()} ` : '';
    const line1 = `${fmt(formData.unitType)}${fmt(formData.unitNumber)}${fmt(formData.floorType)}${fmt(formData.floorNumber)}${fmt(formData.streetLotNumber)}${fmt(formData.streetName)}${fmt(formData.streetType)}`.trim();
    const line3 = `${fmt(formData.suburb)}${fmt(formData.state)}${fmt(formData.postcode)}`.trim();
    return `${line1} ${line3}`.trim();
  };

  const handleFloorTypeChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, floorType: value }));
    setShowFloorNumber(value === 'FL' || value === 'L');
  };

  // Switching verification method discards the tab being left (values and error),
  // so neither the inline message nor the error summary reports a hidden field.
  const handleVerificationMethodChange = (method) => {
    setVerificationMethod(method);
    if (method === 'cardNumber') {
      setFormData(prev => ({ ...prev, ...EMPTY_ADDRESS }));
      setShowManualAddress(false);
      setShowFloorNumber(false);
      setFieldError('address', '');
      dropSummaryError('address');
    } else {
      setFormData(prev => ({ ...prev, cardNumber: '' }));
      setFieldError('cardNumber', '');
      dropSummaryError('cardNumber');
    }
  };

  // Keep focus put on mousedown: otherwise the focused field blurs, its inline error
  // pushes the tabs down, and the shifted layout swallows the tab click.
  const holdFocusOnTabMouseDown = (e) => e.preventDefault();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationError('');

    const newErrors = {};
    ['firstName', 'lastName', 'email', 'phone', 'licenceNumber', 'cardNumber', 'address'].forEach(name => {
      const error = getFieldError(name, formData[name]);
      if (error) newErrors[name] = error;
    });

    if (Object.keys(newErrors).length > 0) {
      shouldFocusErrors.current = true;
      setErrors(newErrors);
      setSummaryErrors(newErrors);
      return;
    }
    setSummaryErrors({});

    const existingOrderData = sessionStorage.getItem('plateOrderData');
    if (!existingOrderData) {
      navigate('/restyle/select-a-style');
      return;
    }

    // Strip leading zeros before any API call or session write.
    const licenceNumber = formData.licenceNumber.replace(/^0+/, '') || formData.licenceNumber;

    setValidating(true);
    let postalAddress = null;
    try {
      if (verificationMethod === 'cardNumber') {
        const result = await executeMutation(validateCustomerByCardNumber, {
          input: {
            ClientId: licenceNumber,
            CardNumber: formData.cardNumber,
            LastName: formData.lastName
          }
        });
        if (!result?.validateCustomerByCardNumber?.IsVerified) {
          setValidationError(ve.customerValidation || 'Details could not be verified. Check they match the plate owner’s driver licence exactly.');
          return;
        }
        postalAddress = result?.validateCustomerByCardNumber?.PostalAddress ?? null;
      } else {
        let addressDpid = formData.addressId;
        if (!showManualAddress) {
          try {
            const addressDetailsResult = await executeMutation(getAddressDetails, { input: { MonikerId: formData.addressId } });
            addressDpid = addressDetailsResult?.getAddressDetails?.Result?.Dpid || formData.addressId;
          } catch (err) {
            // fall through and use Moniker as AddressId
          }
        }

        const validateInput = {
          AddressDetails: {
            AddressId: addressDpid,
            Address: showManualAddress ? buildManualAddress() : formData.address
          },
          CustomerDetails: {
            UserId: "public",
            OfficeId: "0880",
            UserAccessType: "1",
            LastName: formData.lastName,
            ClientId: licenceNumber
          }
        };

        const result = await executeMutation(validateCustomer, { input: validateInput });
        const status = result?.validateCustomer?.Status;
        if (status !== 1 && status !== true && status !== '1') {
          setValidationError(ve.customerValidation || 'Unable to validate your customer details. Please check your information and try again.');
          return;
        }
      }

      const ownershipResult = await executeQuery(checkOwnership, {
        PlateCombo: combination,
        VehicleType: vehicleType === 'car' ? 1 : 2,
        CustomerNumber: licenceNumber,
      });
      const ownershipStatus = ownershipResult?.getPlateCheckOwnership?.Status;
      if (ownershipStatus !== 1 && ownershipStatus !== true && ownershipStatus !== '1') {
        setValidationError(ve.ownershipFailed || 'We could not confirm ownership of this plate. Please check your details and try again.');
        return;
      }
    } catch (err) {
      const errMsg = typeof err?.message === 'string'
        ? err.message
        : err?.errors?.[0]?.message || (ve.genericError || 'An error occurred while validating your details. Please try again.');
      setValidationError(errMsg);
      return;
    } finally {
      setValidating(false);
    }

    const orderData = JSON.parse(existingOrderData);
    orderData.personalDetails = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone,
      licenceNumber,
      verificationMethod,
      cardNumber: verificationMethod === 'cardNumber' ? formData.cardNumber : undefined,
      address: showManualAddress ? buildManualAddress() : formData.address,
      newsletterOptIn: formData.newsletterOptIn,
    };
    if (postalAddress) {
      orderData.shippingAddress = postalAddress;
    }

    sessionStorage.setItem('plateOrderData', JSON.stringify(orderData));
    navigate('/restyle/checkout');
  };

  const inputField = ({ label, id, name, type = 'text', placeholder, required, error, pattern, maxLength, inputMode, instruction, instructionAction, hideLabel = false, inputClasses = '', containerClasses = '', readOnly = false }) => (
    <div className={`input${error ? ' input--error' : ''}${containerClasses ? ' ' + containerClasses : ''}`}>
      <div className={`input__header${hideLabel ? ' visuallyhidden' : ''}`}>
        <label className="input__label" htmlFor={id}>{label}</label>
      </div>
      {instruction ? <p id={`${id}-instruction`} className="input__instruction">{instruction}{instructionAction ? <>{' '}{instructionAction}</> : null}</p> : null}
      <input
        className={`input__ctrl${inputClasses ? ' ' + inputClasses : ''}`}
        type={type}
        id={id}
        name={name}
        placeholder={placeholder}
        value={formData[name] || ''}
        onChange={handleChange}
        onBlur={handleBlur}
        required={required}
        pattern={pattern}
        maxLength={maxLength}
        inputMode={inputMode}
        readOnly={readOnly}
        aria-describedby={instruction ? `${id}-instruction` : undefined}
      />
      {error ? (
        <div className="input__error-container">
          <div className="input__error"><p>{error}</p></div>
        </div>
      ) : null}
    </div>
  );

  const selectField = ({ label, id, name, required, error, options, onChange, value, containerClasses = '' }) => (
    <div className={`input${error ? ' input--error' : ''}${containerClasses ? ' ' + containerClasses : ''}`}>
      <label className="input__label" htmlFor={id}>{label}</label>
      <select
        className="input__ctrl input__ctrl--select"
        id={id}
        name={name}
        required={required}
        value={value !== undefined ? value : formData[name] || ''}
        onChange={onChange || handleChange}
      >
        <option disabled value="">{c.selectPlaceholder || 'Please select...'}</option>
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      {error ? (
        <div className="input__error-container">
          <div className="input__error"><p>{error}</p></div>
        </div>
      ) : null}
    </div>
  );

  return (
    <div id="content" className={styles.page}>
      <div className="row">
        <div className="page-header page-header--small">
          <div className="page-header__content">
            <h1 className="page-header__title">{c.pageTitle || 'Restyle a plate'}</h1>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="plate-wizard">
          <Wizard
            steps={wizardSteps}
            currentStep={2}
            backLink="/restyle/select-a-style"
          />

          <div className="plate-wizard__form plate-wizard__form--identification">
            <div className="plate-wizard__form-header">
              <h2 className="plate-wizard__form-title" tabIndex="-1">{c.sectionTitle || 'Confirm personal details'}</h2>
            </div>

            <div className="identification-forms">
              {validationError && (
                <div className="error-bar error-bar--lock-failed error-bar--warning-style" role="alert" tabIndex="-1">
                  <p className="error-bar__message">
                    <span>{validationError}</span>
                  </p>
                </div>
              )}
              <div className="identification-forms__inner-container identification-forms__inner-container--no-flex">
                <div className="identification-forms__forms">
                  <div className="disclaimer">
                    <p>{c.disclaimer || 'Enter your details exactly as shown on your Victorian driver licence.'}</p>
                  </div>

                  <form onSubmit={handleSubmit} className="form form--bypass-identification-confirmation" tabIndex="-1" noValidate>

                    <div className="form__header form__header--confirm-only hidden">
                      <h4 className="form__header-subtitle">Your details are confirmed</h4>
                      <p>Please review your details</p>
                    </div>

                    <input type="hidden" name="productType" value="Restyle" />
                    {Object.keys(summaryErrors).length > 0 && (
                      <div className="form-error" role="alert" tabIndex="-1" ref={errorSummaryRef}>
                        <p className="form-error__primary-area">
                          {c.formErrorsPrefix || 'Please review the following areas of your form before submitting...'}
                        </p>
                        <div className="form-error__list">
                          <ul>
                            {Object.keys(FIELD_META).filter(field => summaryErrors[field]).map(field => {
                              const meta = FIELD_META[field];
                              const fixed = !errors[field];
                              return (
                                <li key={field} className={`form-error__list-item${fixed ? ' form-error__list-item--fixed' : ''}`}>
                                  {fixed ? <span className="visuallyhidden">Fixed: </span> : null}
                                  <a className="form-error__list-item-link" href={meta.href} onClick={handleErrorClick}>
                                    {meta.label}
                                  </a>
                                  <span className="form-error__list-item-message"> – {errors[field] || summaryErrors[field]}</span>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      </div>
                    )}
                    <div className="form__content">

                      <div className="form__input-group">
                        {inputField({ label: f.firstName?.label || "Plate Owner's first name", id: 'manual-first-name', name: 'firstName', placeholder: f.firstName?.placeholder || 'E.g. Graham', required: true, error: errors.firstName, pattern: "^[a-zA-Z\\-–—'\\s]+$", maxLength: 22, inputClasses: 'mc-opt-in-fname' })}
                        {inputField({ label: f.lastName?.label || "Plate Owner's last name", id: 'manual-last-name', name: 'lastName', placeholder: f.lastName?.placeholder || 'E.g. Smith', required: true, error: errors.lastName, pattern: "^[a-zA-Z\\-–—'\\s]+$", maxLength: 40, inputClasses: 'mc-opt-in-lname' })}
                      </div>

                      <div className="form__input-group">
                        {inputField({ label: f.email?.label || 'Email address', id: 'manual-email-address', name: 'email', type: 'email', placeholder: f.email?.placeholder || 'E.g. email@mail.com', required: true, error: errors.email, inputClasses: 'mc-opt-in-email' })}
                        {inputField({ label: f.phone?.label || 'Phone number', id: 'manual-phone', name: 'phone', type: 'tel', placeholder: f.phone?.placeholder || 'E.g. 0404 444 444', required: true, error: errors.phone, pattern: '^(((0|\\+61)(2|4|3|7|8))|(1300|1800)){0,1}([\\s\\-]*([0-9][\\s\\-]*){8,15}){1}$' })}
                      </div>

                      <div className="form__input-group">
                        {inputField({ label: f.licenceNumber?.label || 'Victorian driver licence number', id: 'manual-licence-number', name: 'licenceNumber', placeholder: f.licenceNumber?.placeholder || 'E.g. 001234567', required: true, error: errors.licenceNumber, pattern: '\\d{3,9}', maxLength: 9, inputMode: 'numeric', instruction: c.licenceSublabel })}
                        <dl className="input input--static">
                          <dt className="input__header">
                            <span className="input__label">{f.plateNumber?.label || 'Plate number'}</span>
                          </dt>
                          <dd className="input__ctrl input__ctrl--combo-from-cart input__ctrl--combo-from-cart-static">
                            <span className="input__ctrl-combo">{combination}</span>
                            <span className="input__owned">{f.plateNumber?.ownedLabel || 'Owned'}</span>
                          </dd>
                        </dl>
                      </div>

                      <fieldset className="form__input-group form__input-group--fieldset">
                        <legend className="form__fieldset-title form__fieldset-title--margin-bottom">{c.verificationMethodLabel || 'Choose an identity verification method'}</legend>
                        <div className="form__tabset">
                          <div className="input input--tab input--small-margin">
                            <input
                              className="input__checkradio-ctrl input__checkradio-ctrl--address-or-card visuallyhidden visuallyhidden--no-focus"
                              type="radio"
                              value="address"
                              name="VerificationMethod"
                              id="verification-method-address"
                              checked={verificationMethod === 'address'}
                              onChange={() => handleVerificationMethodChange('address')}
                            />
                            <label className="input__checkradio-label input__checkradio-label--tab" htmlFor="verification-method-address" onMouseDown={holdFocusOnTabMouseDown} style={verificationMethod === 'address' ? { backgroundColor: '#32831f', color: '#fff' } : undefined}>
                              <span className="input__checkradio-label-text">{c.addressTabLabel || 'Address'}</span>
                            </label>
                          </div>
                          <div className="input input--tab input--small-margin">
                            <input
                              className="input__checkradio-ctrl input__checkradio-ctrl--address-or-card input__checkradio-ctrl--card-number visuallyhidden visuallyhidden--no-focus"
                              type="radio"
                              value="cardNumber"
                              name="VerificationMethod"
                              id="verification-method-address-card-number"
                              checked={verificationMethod === 'cardNumber'}
                              onChange={() => handleVerificationMethodChange('cardNumber')}
                            />
                            <label className="input__checkradio-label input__checkradio-label--tab" htmlFor="verification-method-address-card-number" onMouseDown={holdFocusOnTabMouseDown} style={verificationMethod === 'cardNumber' ? { backgroundColor: '#32831f', color: '#fff' } : undefined}>
                              <span className="input__checkradio-label-text">{c.cardNumberTabLabel || 'Licence Card Number'}</span>
                            </label>
                          </div>
                        </div>
                      </fieldset>

                      {verificationMethod === 'cardNumber' ? (
                        <div className="form__conditional-section form__conditional-section--no-indent" id="card-number">
                          <div className="form__input-group">
                            {inputField({ label: f.cardNumber?.label || 'Driver licence card number', id: 'manual-card-number', name: 'cardNumber', placeholder: f.cardNumber?.placeholder || 'E.g. P1234567', pattern: '[DP][0-9]{7}', maxLength: 8, error: errors.cardNumber, inputClasses: 'input__ctrl--card-number', instruction: f.cardNumber?.instruction || 'Where do I find the card number?', instructionAction: <CardNumberHelp />, hideLabel: true })}
                          </div>
                        </div>
                      ) : null}

                      {verificationMethod === 'address' ? (
                        <div className="form__conditional-section form__conditional-section--no-indent" id="address-entry">
                          {!showManualAddress ? (
                            <div className="form__conditional-section form__conditional-section--always-on-confirm form__conditional-section--no-indent" id="single-address-line">
                              <div className="form__input-group form__input-group--no-margin-bottom">
                                <div className={`input input--two-col input--small-margin-bottom${errors.address ? ' input--error' : ''}`}>
                                  <div className="input__header visuallyhidden">
                                    <label className="input__label" htmlFor="manual-address-line-1">Address</label>
                                  </div>
                                  <div className="input__sublabel-row">
                                    <p className="input__sublabel">{c.addressSublabel || 'As shown on driver licence'}</p>
                                    <Tooltip
                                      content={<span dangerouslySetInnerHTML={{ __html: c.addressSublabelTooltipHtml || '<p>Please enter your address exactly as displayed on your driver licence. If your address has changed, please ensure you update your address details with us by calling 131 171 or visiting a <strong>VicRoads</strong> Customer Service Centre prior to completing this transaction.</p>' }} />}
                                      type="question"
                                      openOnHover
                                    />
                                  </div>
                                  <AddressLookup
                                    id="manual-address-line-1"
                                    value={formData.address}
                                    onChange={(val) => {
                                      setFormData(prev => ({ ...prev, address: val, addressId: '' }));
                                      if (errors.address !== undefined) {
                                        const err = verificationMethod === 'address' && !showManualAddress
                                          ? (!val ? (fe.address || 'Please provide an address line') : (fe.addressNotFromList || 'Please use a suggested value from the drop down list'))
                                          : '';
                                        setFieldError('address', err);
                                      }
                                    }}
                                    onSelect={(suggestion, display) => {
                                      setFormData(prev => ({ ...prev, address: display, addressId: suggestion.Moniker || '' }));
                                      setFieldError('address', '');
                                    }}
                                    onBlur={() => setFieldError('address', getFieldError('address', formData.address))}
                                    error={errors.address}
                                  />
                                  {errors.address ? (
                                    <div className="input__error-container">
                                      <div className="input__error"><p>{errors.address}</p></div>
                                    </div>
                                  ) : null}
                                </div>
                              </div>
                            </div>
                          ) : null}

                          <div className="input input--entry-only">
                            <input
                              className="input__checkradio-ctrl visuallyhidden visuallyhidden--no-focus"
                              type="checkbox"
                              id="manual-custom-address"
                              name="IsUnstructuredAddress"
                              checked={showManualAddress}
                              onChange={(e) => setShowManualAddress(e.target.checked)}
                            />
                            <label className="input__checkradio-label input__checkradio-label--link-style" htmlFor="manual-custom-address">
                              <span className="input__checkradio-label-text input__checkradio-label-text--closed">{c.manualAddressLabel || 'Enter address manually'}</span>
                              <span className="input__checkradio-label-text input__checkradio-label-text--open">{c.backToAddressLookupLabel || 'Back to address look up'}</span>
                            </label>
                          </div>

                          {showManualAddress ? (
                            <div className="form__conditional-section form__conditional-section--entry-only form__conditional-section--no-indent" id="manual-unstructured-address">
                              <div className="form__input-group">
                                {selectField({ label: f.unitType?.label || 'Unit type', id: 'manual-unstructured-unit-type', name: 'unitType', options: UNIT_TYPES })}
                                {inputField({ label: f.unitNumber?.label || 'Unit number', id: 'manual-unstructured-unit-number', name: 'unitNumber', placeholder: f.unitNumber?.placeholder || 'E.g. 3', maxLength: 20, pattern: '^[0-9a-zA-Z-/,.\\s]*$' })}
                                {selectField({
                                  label: f.floorType?.label || 'Floor type',
                                  id: 'manual-unstructured-floor-number-or-code',
                                  name: 'floorType',
                                  onChange: handleFloorTypeChange,
                                  value: formData.floorType,
                                  options: [
                                    { value: 'B', label: 'Basement' },
                                    { value: 'FL', label: 'Floor' },
                                    { value: 'G', label: 'Ground floor' },
                                    { value: 'L', label: 'Level' },
                                    { value: 'LG', label: 'Lower ground floor' },
                                    { value: 'M', label: 'Mezzanine' },
                                    { value: 'UG', label: 'Upper ground floor' },
                                  ],
                                })}
                                {showFloorNumber ? inputField({ label: f.floorNumber?.label || 'Floor/Level number', id: 'manual-unstructured-floor-level-number', name: 'floorNumber', placeholder: f.floorNumber?.placeholder || 'E.g. 4 or G', maxLength: 3, pattern: '^[0-9a-zA-Z-/,.\\s]*$' }) : null}
                                {selectField({
                                  label: f.streetOrLot?.label || 'Is it a street number or a lot number?',
                                  id: 'manual-unstructured-street-or-lot',
                                  name: 'streetOrLot',
                                  value: formData.streetOrLot,
                                  options: [
                                    { value: 'Street number', label: c.streetNumberLabel || 'Street number' },
                                    { value: 'Lot number', label: c.lotNumberLabel || 'Lot number' },
                                  ],
                                })}
                                {inputField({ label: `${formData.streetOrLot === 'Street number' ? (c.streetNumberLabel || 'Street') : (c.lotNumberLabel || 'Lot')} number`, id: 'manual-unstructured-street-lot-number', name: 'streetLotNumber', placeholder: f.streetLotNumber?.placeholder || 'E.g. 49a', maxLength: 10, pattern: '^[0-9a-zA-Z-/,.\\s]*$', containerClasses: 'input--street-lot-number' })}
                                {inputField({ label: f.streetName?.label || 'Street name', id: 'manual-unstructured-street-name', name: 'streetName', placeholder: f.streetName?.placeholder || 'E.g. Albert', maxLength: 26, pattern: '^[0-9a-zA-Z-\\/,.\\s]*$' })}
                                {selectField({ label: f.streetType?.label || 'Street type', id: 'manual-unstructured-street-type', name: 'streetType', required: true, options: STREET_TYPES })}
                                {inputField({ label: f.suburb?.label || 'Suburb', id: 'manual-unstructured-suburb', name: 'suburb', placeholder: f.suburb?.placeholder || 'Broadmeadows', required: true, containerClasses: 'input--two-col', inputClasses: 'input__ctrl--two-col' })}
                                {inputField({ label: f.postcode?.label || 'Postcode', id: 'manual-unstructured-postcode', name: 'postcode', placeholder: f.postcode?.placeholder || 'E.g. 3000', maxLength: 4, pattern: '\\d{4}', required: true, inputMode: 'numeric' })}
                                {selectField({
                                  label: f.state?.label || 'State',
                                  id: 'manual-unstructured-state',
                                  name: 'state',
                                  required: true,
                                  value: formData.state,
                                  options: [
                                    { value: 'ACT', label: 'ACT' },
                                    { value: 'NSW', label: 'NSW' },
                                    { value: 'NT', label: 'NT' },
                                    { value: 'QLD', label: 'QLD' },
                                    { value: 'SA', label: 'SA' },
                                    { value: 'TAS', label: 'TAS' },
                                    { value: 'VIC', label: 'VIC' },
                                    { value: 'WA', label: 'WA' },
                                  ],
                                })}
                                <button type="button" className="manual-address-close" onClick={() => setShowManualAddress(false)}>
                                  {c.backToAddressLookupLabel || 'Back to address look up'}
                                </button>
                              </div>
                            </div>
                          ) : null}
                        </div>
                      ) : null}

                      <div className="input input--entry-only">
                        <input
                          className="input__checkradio-ctrl visuallyhidden visuallyhidden--no-focus"
                          type="checkbox"
                          id="mc-opt-in-check"
                          name="newsletterOptIn"
                          checked={formData.newsletterOptIn}
                          onChange={handleChange}
                        />
                        <label className="input__checkradio-label input__checkradio-label--small" htmlFor="mc-opt-in-check">
                          <span className="input__checkradio-label-text">
                            {c.newsletterOptInHtml
                              ? <span dangerouslySetInnerHTML={{ __html: c.newsletterOptInHtml }} />
                              : <span>I agree to receiving news and promotions about VicRoads Custom Plates and related partner products. View our <a href="/privacy-collection-notice" target="_blank" rel="noopener">Collection Notice</a>&nbsp;and <a href="/privacy" target="_blank" rel="noopener">Privacy Policy</a>&nbsp;for details.</span>
                            }
                          </span>
                        </label>
                      </div>

                    </div>

                    <div className="form__actions">
                      <button type="submit" className="button button--full-width button--entry-only button--identification-entry" disabled={validating}>
                        <span className="button__text">{validating ? (c.validatingText || 'Validating...') : (c.submitText || 'Confirm')}</span>
                      </button>
                      <a href="/restyle/checkout" className="hidden button button--confirm-only button--full-width">Continue</a>
                      <button type="button" className="button button--secondary button--confirm-only button--identification-back button--full-width hidden">
                        <span className="button__text">Back</span>
                      </button>
                    </div>

                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RestyleIdentificationPage;
